/**
 * Multi-series SVG chart, ported from the kit's `chart/*` and extended for the redesign
 * (redesign § Overview chart, § Follower growth): mixed bars + lines, any number of active series,
 * hover focus line and a dark tooltip whose content the caller supplies (server display strings).
 *
 * Geometry only (§12.5): raw `value`s position marks; every string a user reads inside the
 * tooltip comes from the caller. Bars and lines get independent y-scales — a 3-post bar must not
 * flatten under a 900-reaction line. Line ticks label the left edge; bar ticks the right, only
 * when both families are present. A series may override its group with `axis`, which is how a
 * chart whose subject is a line puts it on the bar-side scale without inheriting the 4× headroom.
 */

import { useId, useState, type MouseEvent, type ReactNode } from 'react';
import { cx } from '../cx';
import { axisTicks, formatTick, niceMax } from './chartMath';
import { useContainerWidth } from './useContainerWidth';
import './chart.css';

export type ChartSeries = {
  key: string;
  type: 'bar' | 'line';
  /** Resolved CSS color (e.g. `var(--series-reactions)`). */
  color: string;
  /** One entry per bucket; `null` renders a gap. */
  values: readonly (number | null)[];
  /** Fill the area under the line (profile follower chart). */
  area?: boolean;
  /**
   * Which y-scale this series is measured against. Default `bar → 'right'`, `line → 'left'`,
   * which is the historical two-family split. The right group carries the 4× bar headroom; the
   * left group does not, so a line moved to `'right'` shares the bar scale and a chart of only
   * right-axis lines simply gets a normal `niceMax` (no headroom, no second axis).
   */
  axis?: 'left' | 'right';
  /**
   * Per-bucket "this mark covers an incomplete span" flag, aligned with `values` (SPEC
   * `Content Performance` decision 8: LinkedIn's own `partial` on a trailing week).
   *
   * Purely additive: a series that omits it renders exactly as before. A flagged bar is hatched,
   * a flagged line point keeps a hollow dashed dot even when dots are otherwise hidden — the
   * data's own flag is surfaced, never hidden. The *words* for it belong to the caller's tooltip.
   */
  partial?: readonly boolean[];
};

export type ChartProps = {
  /** Pre-formatted axis labels, one per bucket (server strings). */
  labels: readonly string[];
  series: readonly ChartSeries[];
  height?: number;
  /** Dark-tooltip body for bucket `i`; `null` hides the tooltip for that bucket. */
  renderTooltip?: (index: number) => ReactNode;
  /** Makes buckets clickable (the day/bucket posts popup); the caller owns what a click does. */
  onBucketClick?: (index: number) => void;
  /** Which buckets accept a click — drives the pointer cursor as well as the handler. */
  isBucketClickable?: (index: number) => boolean;
  ariaLabel?: string;
};

const PAD_TOP = 10;
const PAD_BOTTOM = 26;

export function Chart({
  labels,
  series,
  height = 300,
  renderTooltip,
  onBucketClick,
  isBucketClickable,
  ariaLabel,
}: ChartProps) {
  const [hover, setHover] = useState<number | null>(null);
  const [wrapRef, W] = useContainerWidth();
  // One pattern per chart instance: two charts on the same screen must not share an id.
  const hatchId = `chart-hatch-${useId().replace(/[^a-zA-Z0-9-]/g, '')}`;

  const n = labels.length;
  const bars = series.filter((s) => s.type === 'bar');
  const lines = series.filter((s) => s.type === 'line');

  const axisOf = (s: ChartSeries): 'left' | 'right' =>
    s.axis ?? (s.type === 'bar' ? 'right' : 'left');
  const left = series.filter((s) => axisOf(s) === 'left');
  const right = series.filter((s) => axisOf(s) === 'right');

  const rawMaxOf = (list: readonly ChartSeries[]) =>
    list.reduce((max, s) => s.values.reduce<number>((m, v) => (v !== null && v > m ? v : m), max), 0);
  // Bars get 4× headroom so the tallest sits around a quarter of the plot: they are context for
  // the lines, not the subject. Pure geometry — the right-axis ticks still label the real scale.
  // Lines sharing the right axis take the raw max, so a right-axis line chart is not inflated.
  const rightMax = niceMax(
    Math.max(
      rawMaxOf(right.filter((s) => s.type === 'bar')) * 4,
      rawMaxOf(right.filter((s) => s.type === 'line')),
    ),
  );
  // Left ticks: the left group when it has members, else the right group's scale.
  const leftMax = left.length > 0 ? niceMax(rawMaxOf(left)) : rightMax;
  const showRightAxis = left.length > 0 && right.length > 0;
  const leftTicks = axisTicks(leftMax);
  const rightTicks = showRightAxis ? axisTicks(rightMax) : [];

  const padL = 12 + Math.max(...leftTicks.map((t) => formatTick(t).length), 2) * 6.5;
  const padR = showRightAxis
    ? 12 + Math.max(...rightTicks.map((t) => formatTick(t).length), 2) * 6.5
    : 8;

  const H = height;
  const innerW = Math.max(W - padL - padR, 1);
  const innerH = H - PAD_TOP - PAD_BOTTOM;
  const slot = n > 0 ? innerW / n : innerW;
  const xCenter = (i: number) => padL + slot * i + slot / 2;
  const yLeft = (v: number) => PAD_TOP + innerH * (1 - v / leftMax);
  const yRight = (v: number) => PAD_TOP + innerH * (1 - v / rightMax);
  const yOf = (s: ChartSeries, v: number) => (axisOf(s) === 'left' ? yLeft(v) : yRight(v));

  const labelStride = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(innerW / 68))));
  const showDots = n <= 40;

  const barW = Math.max(2, Math.min((slot * 0.62) / Math.max(bars.length, 1), 56));
  const barGroupW = barW * bars.length;

  const tooltip = hover !== null && renderTooltip ? renderTooltip(hover) : null;

  // One mousemove resolves the hovered bucket from the x offset — a hit rect per bucket would put
  // O(buckets) extra nodes in the DOM. clientX is scaled from CSS pixels into viewBox units.
  const bucketFromEvent = (event: MouseEvent<SVGSVGElement>): number | null => {
    const box = event.currentTarget.getBoundingClientRect();
    if (box.width === 0 || n === 0) return null;
    const x = ((event.clientX - box.left) / box.width) * Math.max(W, 1);
    const index = Math.floor((x - padL) / slot);
    return index >= 0 && index < n ? index : null;
  };

  const clickable = (index: number | null): boolean =>
    onBucketClick !== undefined && index !== null && (isBucketClickable?.(index) ?? true);

  /** Anchor the tooltip at the topmost active mark of the hovered bucket. */
  const tooltipY = (index: number): number => {
    let y = PAD_TOP + innerH * 0.25;
    for (const s of series) {
      const v = s.values[index];
      if (v === null || v === undefined) continue;
      const candidate = yOf(s, v);
      if (candidate < y) y = candidate;
    }
    return y;
  };

  return (
    <div ref={wrapRef} className="chart-frame" role="img" aria-label={ariaLabel}>
      <svg
        width="100%"
        height={H}
        viewBox={`0 0 ${Math.max(W, 1)} ${H}`}
        onMouseMove={(event) => setHover(bucketFromEvent(event))}
        onMouseLeave={() => setHover(null)}
        onClick={(event) => {
          const index = bucketFromEvent(event);
          if (index !== null && clickable(index)) onBucketClick?.(index);
        }}
        style={clickable(hover) ? { cursor: 'pointer' } : undefined}
      >
        <defs>
          {/* Diagonal hatch for partial buckets; drawn in the card colour so it reads as
              "incomplete" over any series tint. */}
          <pattern
            id={hatchId}
            width="5"
            height="5"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <line className="bar-hatch" x1="0" y1="0" x2="0" y2="5" />
          </pattern>
        </defs>

        {/* y gridlines + left tick labels */}
        {leftTicks.map((t, i) => {
          const y = yLeft(t);
          return (
            <g key={`y${String(i)}`}>
              <line
                className={t === 0 ? 'baseline' : 'gridline'}
                x1={padL}
                x2={padL + innerW}
                y1={y}
                y2={y}
              />
              <text className="axis-label" x={0} y={y + 3.5} textAnchor="start">
                {formatTick(t)}
              </text>
            </g>
          );
        })}
        {/* right tick labels (bar scale) */}
        {rightTicks.map((t, i) => (
          <text
            key={`yr${String(i)}`}
            className="axis-label"
            x={W}
            y={yRight(t) + 3.5}
            textAnchor="end"
          >
            {formatTick(t)}
          </text>
        ))}

        {/* bars */}
        {bars.map((s, bi) =>
          s.values.map((v, i) => {
            if (v === null) return null;
            const h = PAD_TOP + innerH - yOf(s, v);
            const x = padL + slot * i + (slot - barGroupW) / 2 + barW * bi;
            const partial = s.partial?.[i] === true;
            const opacity = hover === null || hover === i ? 1 : 0.35;
            const geometry = {
              x,
              y: PAD_TOP + innerH - h,
              width: barW,
              height: Math.max(0.5, h),
              rx: 2,
            };
            return (
              <g key={`${s.key}-${String(i)}`}>
                <rect
                  className={cx('bar', partial && 'bar--partial')}
                  {...geometry}
                  style={{ fill: s.color, opacity }}
                />
                {partial ? (
                  <rect
                    className="bar bar--hatch"
                    {...geometry}
                    style={{ fill: `url(#${hatchId})`, opacity }}
                  />
                ) : null}
              </g>
            );
          }),
        )}

        {/* lines: consecutive non-null segments, gaps at nulls */}
        {lines.map((s) => {
          const segments: [number, number][][] = [];
          let current: [number, number][] = [];
          s.values.forEach((v, i) => {
            if (v === null) {
              if (current.length > 0) segments.push(current);
              current = [];
              return;
            }
            current.push([xCenter(i), yOf(s, v)]);
          });
          if (current.length > 0) segments.push(current);

          return (
            <g key={s.key}>
              {s.area
                ? segments.map((points, si) => {
                    if (points.length < 2) return null;
                    const path = points
                      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${String(p[0])} ${String(p[1])}`)
                      .join(' ');
                    const first = points[0];
                    const last = points[points.length - 1];
                    if (first === undefined || last === undefined) return null;
                    const floor = PAD_TOP + innerH;
                    return (
                      <path
                        key={`a${String(si)}`}
                        className="line-area"
                        style={{ fill: s.color, opacity: 0.12 }}
                        d={`${path} L ${String(last[0])} ${String(floor)} L ${String(first[0])} ${String(floor)} Z`}
                      />
                    );
                  })
                : null}
              {segments.map((points, si) => {
                if (points.length === 0) return null;
                if (points.length === 1) {
                  const p = points[0];
                  return p === undefined ? null : (
                    <circle
                      key={`s${String(si)}`}
                      className="line-dot"
                      style={{ stroke: s.color }}
                      cx={p[0]}
                      cy={p[1]}
                      r={2.75}
                    />
                  );
                }
                return (
                  <path
                    key={`p${String(si)}`}
                    className="line-path"
                    style={{ stroke: s.color }}
                    d={points
                      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${String(p[0])} ${String(p[1])}`)
                      .join(' ')}
                  />
                );
              })}
              {s.values.map((v, i) => {
                if (v === null) return null;
                const active = hover === i;
                const partial = s.partial?.[i] === true;
                // A partial point always shows its dot: the marking is the point of it.
                if (!showDots && !active && !partial) return null;
                return (
                  <circle
                    key={`d${String(i)}`}
                    className={cx('line-dot', active && 'active', partial && 'line-dot--partial')}
                    style={{ stroke: s.color, fill: active && !partial ? s.color : undefined }}
                    cx={xCenter(i)}
                    cy={yOf(s, v)}
                    r={active ? 3.75 : 2.75}
                  />
                );
              })}
            </g>
          );
        })}

        {/* focus line */}
        {hover !== null ? (
          <line
            className="focus-line"
            x1={xCenter(hover)}
            x2={xCenter(hover)}
            y1={PAD_TOP}
            y2={PAD_TOP + innerH}
          />
        ) : null}

        {/* x labels */}
        {labels.map((label, i) => {
          if (i % labelStride !== 0 && i !== n - 1) return null;
          return (
            <text
              key={`l${String(i)}`}
              className={`axis-label${hover === i ? ' active' : ''}`}
              x={xCenter(i)}
              y={PAD_TOP + innerH + 17}
              textAnchor="middle"
            >
              {label}
            </text>
          );
        })}
      </svg>

      {hover !== null && tooltip !== null && W > 0 ? (
        <div
          className="chart-tooltip visible"
          style={{
            left: `${String((xCenter(hover) / Math.max(W, 1)) * 100)}%`,
            top: `${String((tooltipY(hover) / H) * 100)}%`,
            marginTop: '-12px',
          }}
        >
          {tooltip}
        </div>
      ) : null}
    </div>
  );
}

/** Small legend row for multi-line charts (group follower growth). */
export function ChartLegend({ items }: { items: readonly { label: string; color: string }[] }) {
  return (
    <div className="chart-legend">
      {items.map((item) => (
        <span className="chart-legend__item" key={item.label}>
          <span className="chart-legend__swatch" style={{ background: item.color }} aria-hidden />
          {item.label}
        </span>
      ))}
    </div>
  );
}

export default Chart;
